import {USER_KEYS} from "@/api/userApi";
import MailIcon from "@/assets/dark_gradient/mail.svg?react";
import Button from "@/components/Common/Button";
import FullPageLoader from "@/components/Common/FullPageLoader";
import TextButton from "@/components/Common/TextButton";
import type {UserDetailSerializer} from "@/generated/DjangoTypes";
import useCountdown from "@/hooks/useCountdown";
import useFetchUserFromAuthToken from "@/hooks/useFetchUserFromAuthToken";
import {client} from "@/httpClient";
import {useMutation} from "@tanstack/react-query";
import {createFileRoute} from "@tanstack/react-router";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";

const CONTACT_EMAIL = "finance@refluenced.com";

export const Route = createFileRoute("/_unauthenticated/verify-email")({
  component: VerifyEmail,
});

function VerifyEmail() {
  const {t} = useTranslation(["verify_account", "common"]);

  useTitle(t("email_verification"));

  const {isLoading, accessToken} = useFetchUserFromAuthToken(data => setUser(data));
  const {countdown, isCountdownFinished, reset, isRunning} = useCountdown({from: 30});
  const [user, setUser] = useState<UserDetailSerializer | null>(null);

  const {mutate: resendVerificationEmail, isPending: isResendingEmail} = useMutation({
    mutationKey: USER_KEYS.mUserNewVerificationEmail(user?.id!),
    mutationFn: async () => {
      if (!user?.id || !accessToken) {
        return;
      }

      return client.post(
        USER_KEYS.mUserNewVerificationEmail(user?.id!)[0],
        {},
        {headers: {Authorization: `Bearer ${accessToken}`}},
      );
    },
    onSuccess: () => {
      reset({shouldStartCountdown: true});
    },
  });

  if (isLoading) {
    return <FullPageLoader />;
  }

  return (
    <div className="flex flex-col items-center p-10">
      <div className="border-neutral-grey-light max-w-wide rounded-2.5xl mt-1 flex flex-col gap-10 border px-8 py-5 sm:px-10 md:px-20">
        <div className="flex flex-col gap-5 text-center">
          <MailIcon className="h-8 w-auto shrink-0" />
          <span className="text-neutral-black sm:text-lg">{t("thank_you_for_purchasing_plan")}</span>
          <span className="text-neutral-black sm:text-lg">{t("verification_email_sent")}</span>
          <span className="text-primary-midnight-dark text-4xl font-bold sm:text-5xl">
            {t("login_to_email_to_verify_account")}
          </span>
          <span className="text-neutral-black sm:text-lg">
            {t("verification_email_explanation", {
              email: user?.email,
            })}
          </span>
        </div>
        <div className="flex flex-col items-center gap-2">
          <span className="text-neutral-grey-dark text-center text-sm">{t("email_not_received")}</span>
          {isRunning && <span className="mt-2 font-medium">{t("common:waitXToTryAgain", {seconds: countdown})}</span>}
          <Button
            onClick={() => resendVerificationEmail()}
            isLoading={isResendingEmail}
            isDisabled={!isCountdownFinished && isRunning}
            buttonStyle="Gradient"
            className="w-full max-w-90">
            {t("resend_email")}
          </Button>
        </div>
        <div className="text-neutral-black text-center sm:text-lg">
          {t("contact_team_about_issue")}{" "}
          <TextButton
            text={CONTACT_EMAIL}
            onClick={() => (window.location.href = `mailto:${CONTACT_EMAIL}?subject=${t("email_verification")}`)}
            className="font-normal"
          />
        </div>
      </div>
    </div>
  );
}
